import type { FC, ReactNode } from 'react'

import { ArrowUpRight } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import ReelButton from '@/components/generals/reel-button'
import { CLIENTS, SERVICES, TOOLKIT, siteConfig } from '@/constants/site'

const CREDIT_SHEET = [
    { term: 'Based in', detail: 'Athens, Greece' },
    { term: 'Experience', detail: 'Six years and counting in digital commercials' },
    { term: 'Currently', detail: 'The Lazer Cow' },
    { term: 'Disciplines', detail: SERVICES.map(service => service.name).join(', ') }
]

const About: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='About'
                description='Geo Smyrniotis is a cinematographer, editor and creative director based in Athens, Greece, with six years of experience in digital commercials. Currently at The Lazer Cow.'
            />

            <section id='intro' aria-labelledby='about-title' className='relative px-[var(--site-gutter)] pb-24 pt-32 md:pb-36 md:pt-40'>
                <div className='mx-auto grid max-w-[1680px] gap-14 lg:grid-cols-12 lg:gap-16'>
                    <div className='flex flex-col lg:col-span-7'>
                        <p className='text-[10px] font-medium uppercase tracking-[0.4em] text-white/50'>
                            About
                        </p>

                        <h1
                            id='about-title'
                            className='mt-6 font-heading text-[clamp(3.2rem,9vw,9rem)] font-semibold uppercase leading-[0.86] tracking-[-0.025em] text-bone'
                        >
                            Hello,
                            <span className='block font-normal italic normal-case tracking-[-0.03em] text-ivory'>I&apos;m Geo.</span>
                        </h1>

                        <p className='mt-10 max-w-2xl font-heading text-[clamp(1.3rem,2.2vw,1.9rem)] italic leading-[1.45] text-white/85'>
                            A cinematographer, editor and creative director based in Athens, Greece, with six years of professional experience in digital commercials.
                        </p>

                        <p className='mt-6 max-w-2xl text-[16px] leading-[1.75] text-white/60'>
                            I shoot and cut commercials, fashion films and music videos for brands like Cosmos Sport, Public, Sneaker10 and Kessaris, moving between the camera, the edit and the idea behind both. Whether I am lighting a night exterior or trimming the last frame of a cut-down, the question stays the same: does this make the work land?
                        </p>

                        <dl className='mt-12 grid max-w-2xl border-t border-white/10'>
                            {CREDIT_SHEET.map(row => (
                                <div key={row.term} className='grid gap-1 border-b border-white/10 py-4 sm:grid-cols-[10rem_1fr] sm:gap-6'>
                                    <dt className='text-[10px] font-medium uppercase tracking-[0.32em] text-white/45'>
                                        {row.term}
                                    </dt>
                                    <dd className='font-heading text-[16px] italic text-white/90'>
                                        {row.detail}
                                    </dd>
                                </div>
                            ))}
                        </dl>
                    </div>

                    <div className='lg:col-span-5'>
                        <figure className='relative overflow-hidden rounded-[32px] bg-[#12100e] md:rounded-[48px] lg:sticky lg:top-28'>
                            <img
                                src='/images/geo-smyrniotis-portrait.webp'
                                alt='Geo Smyrniotis on set, checking a shot on the monitor of a cinema camera rig.'
                                width={1365}
                                height={2048}
                                fetchPriority='high'
                                decoding='async'
                                className='aspect-[4/5] w-full object-cover object-[50%_30%]'
                            />

                            <div
                                aria-hidden
                                className='absolute inset-0'
                                style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0) 55%, rgba(10,9,8,0.75) 100%)' }}
                            />
                            <div aria-hidden className='film-grain absolute inset-0 opacity-[0.06] mix-blend-overlay' />

                            <figcaption className='absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 p-6 md:p-8'>
                                <span className='text-[10px] font-medium uppercase tracking-[0.32em] text-white/70'>
                                    Behind the camera
                                </span>
                                <span className='font-heading text-[15px] italic text-white/85'>
                                    {siteConfig.location}
                                </span>
                            </figcaption>
                        </figure>
                    </div>
                </div>
            </section>

            <section id='philosophy' aria-label='Philosophy' className='relative overflow-hidden border-y border-white/[0.06] px-[var(--site-gutter)] py-24 md:py-40'>
                <div aria-hidden className='film-grain pointer-events-none absolute inset-0 opacity-[0.04]' />

                <figure className='relative mx-auto max-w-5xl text-center'>
                    <blockquote className='font-heading text-[clamp(1.9rem,4.4vw,4rem)] italic leading-[1.18] tracking-[-0.01em] text-bone'>
                        &ldquo;The visuals of a commercial matter, but it is just as vital that the work delivers real results.&rdquo;
                    </blockquote>

                    <figcaption className='mt-10 flex items-center justify-center gap-4 text-[10px] font-medium uppercase tracking-[0.4em] text-white/50'>
                        <span aria-hidden className='h-px w-10 bg-white/25' />
                        {siteConfig.name}
                        <span aria-hidden className='h-px w-10 bg-white/25' />
                    </figcaption>
                </figure>
            </section>

            <section id='now' aria-labelledby='now-title' className='relative px-[var(--site-gutter)] py-24 md:py-36'>
                <div className='mx-auto grid max-w-[1680px] gap-10 md:grid-cols-12'>
                    <div className='md:col-span-6'>
                        <p className='text-[10px] font-medium uppercase tracking-[0.4em] text-white/50'>
                            Currently
                        </p>

                        <h2
                            id='now-title'
                            className='mt-5 font-heading text-[clamp(2.6rem,6vw,5.6rem)] font-semibold uppercase leading-[0.9] tracking-[-0.02em] text-bone'
                        >
                            At The
                            <span className='block font-normal italic normal-case tracking-[-0.03em] text-ivory'>Lazer Cow</span>
                        </h2>
                    </div>

                    <div className='flex flex-col justify-end gap-8 md:col-span-5 md:col-start-8'>
                        <p className='text-[16px] leading-[1.75] text-white/60'>
                            Day to day I am part of The Lazer Cow, the Athens production company behind spots for Cosmos Sport, Sneaker10 and Public. I shoot, edit and shape ideas alongside an amazing, talented team, and I take on directing, camera and editing work for brands and artists directly.
                        </p>

                        <a
                            href='https://www.thelazercow.com'
                            target='_blank'
                            rel='noreferrer'
                            className='group inline-flex w-fit items-center gap-2 font-heading text-[18px] italic text-white/85 transition-colors hover:text-white'
                        >
                            thelazercow.com
                            <ArrowUpRight className='size-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5' strokeWidth={1.5} />
                        </a>
                    </div>
                </div>
            </section>

            <section id='toolkit' aria-labelledby='toolkit-title' className='relative border-t border-white/[0.06] px-[var(--site-gutter)] py-24 md:py-36'>
                <div className='mx-auto max-w-[1680px]'>
                    <div className='grid gap-10 md:grid-cols-12'>
                        <div className='md:col-span-6'>
                            <p className='text-[10px] font-medium uppercase tracking-[0.4em] text-white/50'>
                                Programs and software
                            </p>

                            <h2
                                id='toolkit-title'
                                className='mt-5 font-heading text-[clamp(2.6rem,6vw,5.6rem)] font-semibold uppercase leading-[0.9] tracking-[-0.02em] text-bone'
                            >
                                The
                                <span className='block font-normal italic normal-case tracking-[-0.03em] text-ivory'>toolkit</span>
                            </h2>
                        </div>

                        <p className='self-end text-[16px] leading-[1.75] text-white/60 md:col-span-5 md:col-start-8'>
                            The edit, the grade and the finishing all happen in house, so a film keeps one hand on it from the first frame to the final export.
                        </p>
                    </div>

                    <ul className='mt-16 border-t border-white/10 md:mt-20'>
                        {TOOLKIT.map((tool, index) => (
                            <li key={tool.name} className='group grid items-baseline gap-3 border-b border-white/10 py-7 md:grid-cols-12 md:gap-8 md:py-9'>
                                <span className='font-heading text-lg italic text-white/40 md:col-span-1'>
                                    {String(index + 1).padStart(2, '0')}
                                </span>

                                <h3 className='font-heading text-[clamp(1.7rem,3.4vw,3.2rem)] font-semibold uppercase leading-[0.95] tracking-[-0.02em] text-bone transition-transform duration-500 ease-out group-hover:translate-x-3 md:col-span-7'>
                                    {tool.name}
                                </h3>

                                <span className='text-[10.5px] font-medium uppercase tracking-[0.32em] text-white/55 md:col-span-4 md:text-right'>
                                    {tool.use}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            <section id='clients' aria-labelledby='clients-title' className='relative border-t border-white/[0.06] px-[var(--site-gutter)] py-24 md:py-36'>
                <div className='mx-auto max-w-[1680px]'>
                    <p id='clients-title' className='text-[10px] font-medium uppercase tracking-[0.4em] text-white/50'>
                        Worked with and for
                    </p>

                    <ul className='mt-10 flex flex-wrap items-baseline gap-x-6 gap-y-3 md:mt-14 md:gap-x-9 md:gap-y-5'>
                        {CLIENTS.map((client, index) => (
                            <li key={client} className='flex items-baseline gap-6 md:gap-9'>
                                <span className='font-heading text-[clamp(1.9rem,4.6vw,4.4rem)] font-semibold uppercase leading-[1.05] tracking-[-0.02em] text-white/85 transition-colors duration-300 hover:text-ivory'>
                                    {client}
                                </span>

                                {index < CLIENTS.length - 1 && (
                                    <span aria-hidden className='size-1.5 -translate-y-[0.4em] rounded-full bg-white/25' />
                                )}
                            </li>
                        ))}
                    </ul>

                    <div className='mt-16 flex flex-wrap items-center gap-4 md:mt-20'>
                        <ReelButton href='/works'>See the work</ReelButton>
                        <ReelButton href={siteConfig.links.vimeo} external icon='external'>Vimeo</ReelButton>
                    </div>
                </div>
            </section>
        </Layout>
    )
}

export default About
